import { PurchaseOrderImportPreviewRow } from "../../types/purchase-order.type";
import { IncludedPastRowNos, isPastImportable, isRowSaved } from "./branchChoice";
import { BRANCH_REQUIRED_RESULT, DUPLICATE_RESULT } from "./constants";

/**
 * การติ๊กเลือกหลายแถวในตารางตัวอย่างของ W2 — ตัวช่วยล้วน ๆ
 *
 * เลือกได้ทุกแถวที่บันทึกได้ (ไม่ใช่แถวซ้ำ) · ชุดที่เลือกเป็น Set ของ row_no ที่อยู่ข้าม
 * หน้า ข้ามการค้นหา และข้ามการเปลี่ยนตัวกรอง · ไฟล์จริงมี ~8,700 แถว ทุกอย่างในนี้จึง
 * เป็น O(n) ครั้งเดียวต่อการกด ไม่ใช่ต่อแถว
 */

/** แถวที่ติ๊กเลือกได้ — แถวซ้ำไม่ถูกบันทึกไม่ว่าจะทำอะไร จึงไม่มีอะไรให้ทำกับมัน */
export const isSelectableRow = (row: PurchaseOrderImportPreviewRow): boolean =>
  row.result !== DUPLICATE_RESULT;

/** สลับแถวเดียว */
export const toggleRowSelection = (
  current: ReadonlySet<number>,
  rowNo: number,
): Set<number> => {
  const next = new Set(current);
  if (next.has(rowNo)) next.delete(rowNo);
  else next.add(rowNo);
  return next;
};

/** เลือก (select=true) หรือเอาออก (false) ทั้งชุด */
export const setRowsSelection = (
  current: ReadonlySet<number>,
  rowNos: readonly number[],
  select: boolean,
): Set<number> => {
  const next = new Set(current);
  rowNos.forEach((rowNo) => {
    if (select) next.add(rowNo);
    else next.delete(rowNo);
  });
  return next;
};

/**
 * Shift+คลิก: ทุกแถวระหว่างแถวที่คลิกล่าสุด (anchor) กับแถวที่คลิกตอนนี้ (target) ตาม
 * ลำดับที่กรองอยู่ ได้สถานะเดียวกับที่ target กำลังจะเป็น (เลือก ↔ เอาออก) — เหมือน
 * Gmail/Explorer · anchor ไม่อยู่ในชุดที่กรอง (หรือไม่มี) = สลับเฉพาะ target
 *
 * `orderedRowNos` ต้องเป็นแถวที่เลือกได้ของชุดที่กรองอยู่ตามลำดับที่แสดง
 */
export const applyRangeSelection = (
  current: ReadonlySet<number>,
  orderedRowNos: readonly number[],
  anchorRowNo: number | null,
  targetRowNo: number,
): Set<number> => {
  const targetIndex = orderedRowNos.indexOf(targetRowNo);
  const anchorIndex =
    anchorRowNo === null ? -1 : orderedRowNos.indexOf(anchorRowNo);

  if (targetIndex === -1 || anchorIndex === -1) {
    return toggleRowSelection(current, targetRowNo);
  }

  const select = !current.has(targetRowNo);
  const [from, to] =
    anchorIndex <= targetIndex
      ? [anchorIndex, targetIndex]
      : [targetIndex, anchorIndex];

  return setRowsSelection(current, orderedRowNos.slice(from, to + 1), select);
};

/** สิ่งที่ทำกับชุดที่เลือกได้ แต่ละปุ่มนับเฉพาะแถวที่ตัวเองมีผล */
export interface SelectionActions {
  /** แถวเกินกำหนดส่งในชุดที่ยังไม่ได้ติ๊กนำเข้า → ปุ่ม นำเข้า (N) */
  pastToInclude: number[];
  /** แถวเกินกำหนดส่งในชุดที่ติ๊กนำเข้าอยู่ → ปุ่ม ไม่นำเข้า (N) */
  pastToExclude: number[];
  /** แถว ต้องเลือกสาขา ในชุดที่จะถูกบันทึก → ใช้กับแถวที่เลือก (N) */
  branchRequired: number[];
}

export const summarizeSelection = (
  rows: readonly PurchaseOrderImportPreviewRow[],
  selected: ReadonlySet<number>,
  includedPast: IncludedPastRowNos,
): SelectionActions => {
  const actions: SelectionActions = {
    pastToInclude: [],
    pastToExclude: [],
    branchRequired: [],
  };
  if (selected.size === 0) return actions;

  rows.forEach((row) => {
    if (!selected.has(row.row_no)) return;

    if (isPastImportable(row)) {
      if (includedPast.has(row.row_no)) actions.pastToExclude.push(row.row_no);
      else actions.pastToInclude.push(row.row_no);
    }

    // แถวเกินกำหนดส่งที่ยังไม่นำเข้าไม่ต้องมีสาขา จึงยังไม่นับจนกว่าจะกด นำเข้า
    if (
      row.result === BRANCH_REQUIRED_RESULT &&
      isRowSaved(row, includedPast)
    ) {
      actions.branchRequired.push(row.row_no);
    }
  });

  return actions;
};
